import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Card, Grid, Typography } from "@mui/material";
import { useTheme } from "@emotion/react";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import { useTranslation } from "react-i18next";
import { getAllData, addData } from "../../Utility/API";
import { endPoints } from "../../constant/Environment";
import { useSnackbarContext } from "../../component/SnackbarContext";
import HappyHourBanner from "./HappyHourBanner";
import HappyHourTeaser from "./HappyHourTeaser";
import HappyHourLive from "./HappyHourLive";
import HappyHourRecap from "./HappyHourRecap";

const CRIMSON = "#a31545";
const CRIMSON_DEEP = "#7d1733";

// The casinoCoin account API returns the status/recap DTO raw, while some
// deployments wrap it as { status, data }. Accept either shape.
const unwrapStatus = (res) => {
  if (!res || typeof res !== "object") return null;
  if (res.status === "success") return res.data || null;
  if ("state" in res || "live" in res) return res;
  return null;
};

const unwrapRecap = (res) => {
  if (!res || typeof res !== "object") return null;
  if (res.status === "success") return res.data || null;
  if (
    "message" in res ||
    "bonusCoinsEarned" in res ||
    "happyHourBonus" in res ||
    "windowHours" in res
  ) {
    return res;
  }
  return null;
};

/**
 * Orchestrates the player-facing happy-hour surfaces on the lobby:
 *  - an in-flow banner (live countdown / "incoming" teaser),
 *  - a pre-event teaser modal, a live screen modal and a post-event recap modal.
 *
 * Reads GET /happyHour/status/{userId} for live/upcoming state and
 * GET /happyHour/recap/{userId} for the post-event summary. Each modal
 * auto-opens at most once per occurrence (tracked in localStorage) so it is
 * surfaced without nagging; the banner re-opens the relevant modal on click.
 *
 * With `showPromoCard`, it instead renders a clickable Happy Hour tile that fits
 * the All Promotions grid and opens the relevant modal on demand. In that mode
 * the auto-open and in-flow banner are suppressed (those belong to the lobby).
 */
const HappyHour = ({ showPromoCard = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const authState = useSelector((state) => state.auth);
  const { IsLoginData, isAuthenticated } = authState;
  const userId = IsLoginData?.idUser;
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbarContext();

  const [status, setStatus] = useState(null);
  const [recap, setRecap] = useState(null);
  const [activeModal, setActiveModal] = useState(null); // 'teaser' | 'live' | 'recap'

  const fetchStatus = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await getAllData(`${endPoints.api.HAPPY_HOUR_STATUS}/${userId}`);
      setStatus(unwrapStatus(res));
    } catch (e) {
      setStatus(null);
    }
  }, [userId]);

  // Initial + on-auth fetch.
  useEffect(() => {
    if (isAuthenticated && userId) fetchStatus();
  }, [isAuthenticated, userId, fetchStatus]);

  // Pull the recap only when the backend says we're in the recap window.
  useEffect(() => {
    if (!userId || !status?.showRecap) return;
    (async () => {
      try {
        const res = await getAllData(`${endPoints.api.HAPPY_HOUR_RECAP}/${userId}`);
        const data = unwrapRecap(res);
        if (data) setRecap(data);
      } catch (e) {
        /* no-op */
      }
    })();
  }, [userId, status?.showRecap]);

  // Auto-open a surface right after a fresh login, at most once per occurrence.
  const maybeAutoOpen = (key, modal) => {
    try {
      // Only surface as a post-login popup — not on every lobby visit.
      if (sessionStorage.getItem("openHappyHourAfterLogin") !== "1") return;
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
      sessionStorage.removeItem("openHappyHourAfterLogin");
    } catch (e) {
      /* storage unavailable — fall through and still open once */
    }
    setActiveModal((prev) => prev || modal);
  };

  useEffect(() => {
    if (showPromoCard || !status || !userId) return;
    const occ = status.startTime ? new Date(status.startTime).getTime() : status.state;
    if (status.live) {
      maybeAutoOpen(`hh_live_${userId}_${occ}`, "live");
    } else if (status.showTeaser) {
      maybeAutoOpen(`hh_teaser_${userId}_${occ}`, "teaser");
    }
  }, [status, userId, showPromoCard]);

  useEffect(() => {
    if (showPromoCard || !recap || !status?.showRecap || !userId) return;
    const dayBucket = Math.floor(Date.now() / 86400000);
    maybeAutoOpen(`hh_recap_${userId}_${dayBucket}`, "recap");
  }, [recap, status?.showRecap, userId, showPromoCard]);

  const handleRemind = (whenLabel) => {
    try {
      if (userId) localStorage.setItem(`hh_reminder_${userId}`, "1");
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        Notification.requestPermission();
      }
    } catch (e) {
      /* no-op */
    }
    showSuccessSnackbar(
      t("happyHour.reminderSet", {
        when: whenLabel ? whenLabel : t("happyHour.reminderDefaultWhen"),
      })
    );
  };

  // Fired from the live modal's "Play now" CTA: claim the bonus/cash drop once
  // for this occurrence, then dismiss. Re-opening shows the disabled state.
  const handlePlayNow = async () => {
    if (!userId) {
      closeModal();
      return;
    }
    if (status?.bonusDropClaimed) {
      closeModal();
      return;
    }
    try {
      const res = await addData(`${endPoints.api.HAPPY_HOUR_CLAIM}/${userId}`, {});
      const body = res?.data;
      if (body?.status === "success") {
        const d = body.data || {};
        const parts = [];
        if (d.bonusDrop) parts.push(`${d.bonusDrop} bonus`);
        if (d.cashDrop) parts.push(`${d.cashDrop} cash`);
        showSuccessSnackbar(
          d.message ? d.message : t("happyHour.claimSuccess")
        );
        fetchStatus();
      } else {
        showErrorSnackbar(body?.message || t("happyHour.claimError"));
      }
    } catch (e) {
      showErrorSnackbar(t("happyHour.claimError"));
    }
    closeModal();
  };

  const closeModal = () => setActiveModal(null);

  if (!isAuthenticated || !userId || !status) return null;

  const multiplier = status.multiplier || status.coinMultiplier || 2;

  // Which surface the entry icon re-opens, given the current state.
  const entryModal = status.live
    ? "live"
    : status.state === "UPCOMING" && status.showTeaser
    ? "teaser"
    : status.showRecap
    ? "recap"
    : null;

  const modals = (
    <>
      <HappyHourTeaser
        open={activeModal === "teaser"}
        onClose={closeModal}
        status={status}
        onRemind={handleRemind}
      />

      <HappyHourLive
        open={activeModal === "live"}
        onClose={closeModal}
        status={status}
        onExpire={fetchStatus}
        onPlayNow={handlePlayNow}
      />

      <HappyHourRecap
        open={activeModal === "recap"}
        onClose={closeModal}
        recap={recap}
        onRemind={handleRemind}
      />
    </>
  );

  if (showPromoCard) {
    // Only surface the tile when there's actually a happy hour to open.
    if (!entryModal) return null;
    const title = status.live
      ? t("happyHour.cardTitleLive")
      : status.showRecap
      ? t("happyHour.cardTitleRecap")
      : t("happyHour.cardTitleDefault");
    return (
      <Grid item xs={6} sm={6} md={3}>
        <Card
          onClick={() => setActiveModal(entryModal)}
          className="c_card middle_card"
          sx={{
            cursor: "pointer",
            background: theme.palette.custom.lgray,
            borderColor: theme.palette.custom.redblue_border,
          }}
        >
          <div className="card_label">
            <p>
              <span style={{ fontSize: "10px", color: "white" }}>
                {status.live ? t("happyHour.cardLiveNow") : ""}
              </span>
            </p>
          </div>

          <div className="buy_card1">
            <div className="right_half">
              <Box
                sx={{
                  height: 88,
                  mb: "8px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  color: "#fff",
                  "@keyframes hhPulse": {
                    "0%, 100%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(255,196,84,0.6)" },
                    "50%": { transform: "scale(1.08)", boxShadow: "0 0 14px 5px rgba(255,196,84,0)" },
                  },
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(145deg, #ffdf7e 0%, #f5a623 100%)",
                    animation: "hhPulse 2.2s ease-in-out infinite",
                    marginTop: 1,
                  }}
                >
                  <BoltRoundedIcon sx={{ fontSize: 26, color: CRIMSON_DEEP }} />
                </Box>
                <Box sx={{ display: "flex", alignItems: "baseline", lineHeight: 1 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: 22, color: "#ffdf7e", textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}>
                    {multiplier}x
                  </Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: 10, ml: 0.5, letterSpacing: 1.2 }}>
                    {t("happyHour.cardCoinsLabel")}
                  </Typography>
                </Box>
              </Box>
            </div>
            <div className="left_half">
              <Typography
                className="f18 f-bold"
                sx={{ color: theme.palette.custom.white_gray }}
                style={{ display: "flex", justifyContent: "center" }}
              >
                {title}
              </Typography>

              <Button
                className="best_btn"
                size="small"
                sx={{
                  background: theme.palette.custom.red,
                  color: theme.palette.custom.white,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveModal(entryModal);
                }}
              >
                {status.live
                  ? t("happyHour.cardClaimNow")
                  : status.showRecap
                  ? t("happyHour.cardViewRecap")
                  : t("happyHour.cardViewRewards")}
              </Button>
            </div>
          </div>
        </Card>
        {modals}
      </Grid>
    );
  }

  const showBanner = status.live || (status.state === "UPCOMING" && status.showTeaser);

  return (
    <>
      {showBanner && (
        <HappyHourBanner
          status={status}
          onExpire={fetchStatus}
          onClick={() => setActiveModal(status.live ? "live" : "teaser")}
        />
      )}

      {modals}
    </>
  );
};

export default HappyHour;
